import type { RegistrationStatus } from "@/modules/events/types/eventDetailsApi"
import type { FunctionComponent } from "react"

import { isAfter } from "date-fns"
import { useParams } from "next/navigation"

import { Callout } from "@jobteaser/spark/components/Callout"
import { Text } from "@jobteaser/spark/components/Text"

import { JOBTEASER } from "@/modules/events/constants"
import { useEventDetailsData } from "@/modules/events/hooks/useEventDetailsData"
import { getConferenceUrlToDisplay, getIsLinkDisplayed } from "@/modules/events/utils/videoConferenceLink"
import { useTranslation } from "@/modules/i18n/components/useTranslation"

import styles from "./StatusConfirmed.module.css"

type StatusConfirmedProps = {
  registrationStatus: RegistrationStatus
}

export const StatusConfirmed: FunctionComponent<StatusConfirmedProps> = ({ registrationStatus }) => {
  const { t } = useTranslation(["event_details"])
  const { eventSlug } = useParams<{ eventSlug: string }>()
  const event = useEventDetailsData(eventSlug)

  if (typeof event === "undefined") {
    return null
  }
  const { startDate } = event

  const {
    registrationDetails,
    location: { type },
  } = event

  if (registrationDetails.type !== JOBTEASER) {
    return null
  }

  const currentDate = new Date()
  const unregistrationDate = new Date(registrationDetails.unregistrationDeadline)
  const isUnregistrationDeadlineExpired = isAfter(currentDate, unregistrationDate)
  const isOnline = type === "online"
  const conferenceUrlToDisplay = getConferenceUrlToDisplay({ event, registrationStatus })
  const isLinkDisplayed = isOnline ? getIsLinkDisplayed({ conferenceUrl: conferenceUrlToDisplay, startDate }) : false

  return (
    <div className={styles.main} data-testid="event-details-registration-details-status-confirmed">
      <Callout variant="success">
        <Text variant="body1" weight="bold">
          {t("event_details.status.confirmed")}
        </Text>
        {isOnline && !isLinkDisplayed && (
          <Text
            className={styles.textInfo}
            variant="body1"
            data-testid="event-details-registration-details-status-confirmed-message"
          >
            {t("event_details.status.confirmed_message.video_conference_link")}
          </Text>
        )}
        {!isOnline && isUnregistrationDeadlineExpired && (
          <Text
            className={styles.textInfo}
            variant="body1"
            data-testid="event-details-registration-details-status-confirmed-not-cancellable"
          >
            {t("event_details.status.confirmed_message.subscription_not_cancellable")}
          </Text>
        )}
      </Callout>
    </div>
  )
}
